import { Dispatch } from 'redux';

import {
    FutureOrderPayload,
    FutureSymbol,
    FutureSymbolSettingsPayload,
    FutureSymbolPositionsPayload,
    CancelOrderPayload,
    FundingRate,
    ActiveOrder,
    Position,
    FutureTradeAble,
    RiskLimitPayload,
    StopProfitLossPayLoad,
    SetStopProfitLossPayLoad,
    CalculatorQueryParamsType,
    ModifyPositionMarginPayLoad,
    ToastBody,
    SetLeveragePayload,
    GetLeveragePayload,
} from 'src/types';

import * as requestFromServer from './derivativePageCrud';
import { derivativePageSlice, callTypes } from './derivativePageSlice';

const { actions } = derivativePageSlice;

export const fetchFundingRate = (queryParams: string) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.fetchFundingRate(queryParams);
        dispatch(actions.fundingRateFetched(response.data[0]));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const updateFundingRate = (fundingRate: FundingRate) => (dispatch: Dispatch) => {
    dispatch(actions.fundingRateFetched(fundingRate));
};

export const fetchAvailableAssets = () => async (dispatch: Dispatch) => {
    try {
        const response = await requestFromServer.fetchAvailableAssets();
        dispatch(actions.availableAssetsFetched(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const fetchSymbolSettings =
    (body: FutureSymbolSettingsPayload) => async (dispatch: Dispatch) => {
        dispatch(actions.startCall(callTypes.action));
        try {
            const response = await requestFromServer.fetchSymbolSettings(body);
            dispatch(actions.symbolSettingsFetched(response.data[0]));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };

export const fetchSymbolPositions =
    (body: FutureSymbolPositionsPayload) => async (dispatch: Dispatch) => {
        dispatch(actions.startCall(callTypes.action));
        try {
            const response = await requestFromServer.fetchSymbolPositions(body);
            dispatch(actions.symbolPositionsFetched(response.data));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };

export const createOrder = (body: FutureOrderPayload) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.createOrder(body);
        dispatch(actions.orderCreated({ order: response.data, isFutures: !!body.futures }));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const performCalculations =
    (queryParams: CalculatorQueryParamsType) => async (dispatch: Dispatch) => {
        dispatch(actions.startCall(callTypes.action));
        try {
            const response = await requestFromServer.performCalculations(queryParams);
            dispatch(actions.callEnded(callTypes.action));
            return response.data;
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };

export const setSymbolSettings =
    (body: FutureSymbolSettingsPayload) => async (dispatch: Dispatch) => {
        dispatch(actions.startCall(callTypes.action));
        try {
            await requestFromServer.setSymbolSettings(body);
            const response = await requestFromServer.fetchSymbolSettings(body);
            dispatch(actions.symbolSettingsFetched(response.data[0]));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };

export const saveCurrentSymbol = (symbol: FutureSymbol) => (dispatch: Dispatch) => {
    dispatch(actions.saveCurrentSymbol(symbol));
};

export const fetchPositions = () => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.fetchPositions();
        dispatch(actions.fetchPositions(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const fetchActiveOrders = (queryParams: string) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.fetchOpenOrders(queryParams);
        dispatch(actions.fetchActiveOrders(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const fetchConditionalOrders = (queryParams: string) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.fetchOpenOrders(queryParams);
        dispatch(actions.fetchConditionalOrders(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const fetchTradeOrders = (queryParams: string) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.fetchTradeOrders(queryParams);
        dispatch(actions.fetchTradeOrders(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const fetchMyTrades = (queryParams: string) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.fetchMyTrades(queryParams);
        dispatch(actions.fetchMyTrades(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

// cancel active order
export const cancelActiveOrder = (body: CancelOrderPayload) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.cancelOrder(body);
        dispatch(actions.activeOrderCanceled(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const updateUserTradeData =
    (responseMsg: ActiveOrder | Position | FutureTradeAble, topic: string) =>
    (dispatch: Dispatch) => {
        dispatch(actions.socketUserDataReceived({ data: responseMsg, topic }));
    };

export const updateTvWidgetRef = (tvWidgetRef: any) => (dispatch: Dispatch) => {
    dispatch(actions.updateTvWidgetRef(tvWidgetRef));
};

export const cancelConditionalOrder = (body: CancelOrderPayload) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.cancelOrder(body);
        dispatch(actions.conditionalOrderCanceled(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const updateCreateOrderPrice = (price: string) => (dispatch: Dispatch) => {
    dispatch(actions.updateCreateOrderPrice(price));
};

export const setRiskLimit = (body: RiskLimitPayload) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        await requestFromServer.setRiskLimit(body);
        const response = await requestFromServer.fetchSymbolSettings({
            exchange_id: body.exchange_id,
            symbol_ids: body.symbol_id,
        });
        dispatch(actions.symbolSettingsFetched(response.data[0]));
    } catch (error: any) {
        const { response } = error;
        let title = '';
        let description = '';
        switch (response?.data?.code) {
            case 31023:
                title = 'error_invalid_leverage_title';
                description = 'error_invalid_leverage_description';
                break;
            default:
                dispatch(actions.catchError(error));
                break;
        }
        if (title && description) {
            dispatch(actions.showNotification({ title, description, type: 'error' }));
        }
    }
};

export const fetchPositionStopProfitLoss =
    (body: StopProfitLossPayLoad) => async (dispatch: Dispatch) => {
        dispatch(actions.startCall(callTypes.action));
        try {
            const response = await requestFromServer.fetchPositionStopProfitLoss(body);
            const { is_long, symbol_id } = body;
            const data = { ...response.data, isLong: is_long, symbolId: symbol_id, isSet: false };
            dispatch(actions.savePositionStopLoss(data));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };

export const setPositionStopProfitLoss =
    (body: SetStopProfitLossPayLoad) => async (dispatch: Dispatch) => {
        dispatch(actions.startCall(callTypes.action));
        try {
            const response = await requestFromServer.setPositionStopProfitLoss(body);
            const { is_long, symbol_id } = body;
            const data = { ...response.data, isLong: is_long, symbolId: symbol_id, isSet: true };
            dispatch(actions.savePositionStopLoss(data));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };

export const cancelPositionStopProfitLoss =
    (body: SetStopProfitLossPayLoad) => async (dispatch: Dispatch) => {
        dispatch(actions.startCall(callTypes.action));
        try {
            /* eslint-disable-next-line no-unused-vars */
            const response = await requestFromServer.cancelPositionStopProfitLoss(body);
            const { is_long, symbol_id } = body;
            const data = { isLong: is_long, symbolId: symbol_id };
            dispatch(actions.cancelPositionStopLoss(data));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };

export const removeLoggedInUserData = () => async (dispatch: Dispatch) => {
    dispatch(actions.removeLoggedInUserData());
};

export const modifyPositionMargin =
    (body: ModifyPositionMarginPayLoad) => async (dispatch: Dispatch) => {
        dispatch(actions.startCall(callTypes.action));
        try {
            const response = await requestFromServer.modifyPositionMargin(body);
            const { type } = body;
            const data = { status: response.status, type: type };
            dispatch(actions.positionMarginModify(data));
        } catch (error) {
            dispatch(actions.catchError(error));
        }
    };

export const showNotification = (body: ToastBody) => async (dispatch: Dispatch) => {
    try {
        dispatch(actions.showNotification(body));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const getLeverage = (body: GetLeveragePayload) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        const response = await requestFromServer.getLeverage(body);
        dispatch(actions.leverageFetched(response.data));
    } catch (error) {
        dispatch(actions.catchError(error));
    }
};

export const setLeverage = (body: SetLeveragePayload) => async (dispatch: Dispatch) => {
    dispatch(actions.startCall(callTypes.action));
    try {
        await requestFromServer.setLeverage(body);
        dispatch(actions.showNotification({ title: 'leverage_updated', type: 'success' }));
        dispatch(actions.callEnded(callTypes.action));
    } catch (error: any) {
        const { response } = error;
        let title = '';
        let description = '';
        switch (response?.data?.code) {
            case 32144:
                title = 'error_leverage_high_title';
                description = 'error_leverage_high_description';
                break;
            case 32145:
                title = 'error_invalid_margin_title';
                description = 'error_invalid_margin_description';
                break;
            case 32146:
                title = 'error_leverage_adjustment_title';
                description = 'error_leverage_adjustment_description';
                break;
            case 33001:
                title = 'error_insufficient_fund_title';
                description = 'error_insufficient_fund_description';
                break;
            default:
                dispatch(actions.catchError(error));
                break;
        }
        if (title !== '' && description !== '') {
            dispatch(actions.showNotification({ title, description, type: 'error' }));
        }
    }
};
